/**
 * @fileoverview Checkbox — light-mode intake form checkbox with label + description layout.
 *
 * Renders as a grid: [ checkbox ] [ label ]
 *                                 [ description ]
 *
 * Uses native <input type="checkbox"> for React Hook Form compatibility.
 */
import clsx from 'clsx';
import { Check } from 'lucide-react';
import React, { forwardRef } from 'react';

export interface FormCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text used both visually and for accessibility */
  label: string;
  /** Optional description below the label */
  description?: string;
}

export const FormCheckbox = forwardRef<HTMLInputElement, FormCheckboxProps>(
  ({ className, label, description, checked, ...props }, ref) => (
    <div
      className={clsx(
        className,
        'relative grid grid-cols-[1rem_1fr] gap-x-2.5 gap-y-0.5',
        props.disabled && 'opacity-50',
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        className={clsx(
          'peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0',
          props.disabled && 'cursor-not-allowed',
        )}
        {...props}
      />

      <span
        className={clsx(
          'col-start-1 row-start-1 mt-0.5 flex h-4 w-4 items-center justify-center rounded',
          'border transition-colors duration-100',
          checked
            ? 'border-blue-600 bg-blue-600 text-white'
            : 'border-zinc-300 bg-white text-transparent hover:border-zinc-400',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-1',
        )}
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>

      <span className="col-start-2 row-start-1 text-sm text-zinc-800">{label}</span>

      {description && (
        <span className="col-start-2 row-start-2 text-xs text-zinc-500">
          {description}
        </span>
      )}
    </div>
  ),
);

FormCheckbox.displayName = 'FormCheckbox';
